import React, { useState, useContext, useEffect } from "react";
import { getImageUrlWithToken } from "../../utils/ImageUtils";
import {
  IconButton,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Tooltip,
  Menu,
  MenuItem,
  styled,
  CircularProgress,
  InputBase,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  useMediaQuery,
  Chip,
  ListItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Public,
  People,
  Close,
  ArrowDropDown,
  PhotoLibrary,
  LockPerson,
  BeachAccess,
  VerifiedUser,
} from "@mui/icons-material";
import { UserContext } from "../../context";
import Slide from "@mui/material/Slide";
import { createPost, getPostTypes } from "../../services/FeedApi";
import { getPeople, getHashtags } from "../../services/UserProfileApi";
import { getLinkPreview } from "../../services/SearchApi";
import { compressImage } from "../../utils/ImageCompression";
import CustomEmojiPicker from "../CustomEmojiPicker";
import CustomizedTooltips from "../common/CustomizedTooltips";
import MentorBadge from "../MentorBadge";
import VideoPreview from "../projects/VideoPreview";


const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const MediaPreviewBox = styled(Box)(({ theme }) => ({
  position: "relative",
  borderRadius: theme.spacing(1),
  width: 100,
  height: 100,
  overflow: "hidden",
  "&:hover .media-remove": {
    opacity: 1,
  },
}));

const urlRegex =
  /https?:\/\/(?:www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/\S*)?/gi;

const videoRegex =
  /(youtube\.com|youtu\.be|vimeo\.com|soundcloud\.com|twitter\.com|x\.com|facebook\.com|instagram\.com\/(reel|p|tv))/i;



const MediaPost = ({ openDialog, handleCloseDialog, setPosts, showSnackbar }) => {
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const { user } = useContext(UserContext);
  const [query, setQuery] = useState("");
  const [mediaPreviews, setMediaPreviews] = useState([]);
  const [isPosting, setIsPosting] = useState(false);

  const [privacy, setPrivacy] = useState(user?.post_type || "public");
  const [privacyAnchorEl, setPrivacyAnchorEl] = useState(null);

  const [postText, setPostText] = useState("");

  const handleEmojiSelect = (emoji) => {
    setPostText((prevMsg) => prevMsg + emoji);
  };

  const handlePrivacyClick = (event) => {
    setPrivacyAnchorEl(event.currentTarget);
  };

  const handlePrivacyClose = () => {
    setPrivacyAnchorEl(null);
  };

  const handlePrivacyChange = (newPrivacy) => {
    if (newPrivacy === "connections") {
      newPrivacy = "connection";
    }

    setPrivacy(newPrivacy);
    handlePrivacyClose();
  };

  const [types, setTypes] = useState([]);

  useEffect(() => {
    if (user?.status !== 1) {
      return;
    }
    const fetchPostTypes = async () => {
      try {
        const res = await getPostTypes("all");
        setTypes(res);
      } catch (err) {
        console.error("Failed to fetch post types:", err);
      }
    };
    fetchPostTypes();
  }, []);

  const [selectedPostType, setSelectedPostType] = useState("");

  const handlePostTypeSelect = (type) => {
    setSelectedPostType(type);
  };

  const handleMediaChange = async (e) => {
    const files = Array.from(e.target.files);
    console.log("Files selected:", files);

    // Process each file (compress if it's an image)
    const newPreviews = await Promise.all(
      files.map(async (file) => {
        let processedFile = file;

        // Check if the file is an image and compress it
        if (file.type.startsWith("image/")) {
          try {
            processedFile = await compressImage(file); // Compress image
            console.log("Image compressed:", processedFile);
          } catch (error) {
            console.error("Image compression failed:", error);
          }
        }

        return {
          id: URL.createObjectURL(processedFile), // Temporary unique URL for rendering
          preview: URL.createObjectURL(processedFile),
          type: processedFile.type.split("/")[0],
          file: processedFile, // Store compressed file
        };
      })
    );

    setMediaPreviews((prevPreviews) => [...prevPreviews, ...newPreviews]);
  };

  const handlePost = () => {
    if (!postText.trim() && mediaPreviews.length === 0) return;

    const mentionedUsernames = postText.match(/@([\w.]+)/g);
    console.log("mentionedUsernames", mentionedUsernames);
    const mentionedUserIds = [];
    // Combine taggedUsers and allSuggestions for lookup
    const combinedUsers = [...taggedUsers, ...allSuggestions];
    if (mentionedUsernames) {
      mentionedUsernames.forEach(mention => {
        const username = mention.substring(1); // Remove @ symbol
        const foundUser = combinedUsers.find(person => person.username === username);
        console.log(`Processing mention: ${mention}, found user:`, foundUser);
        if (foundUser && !mentionedUserIds.includes(foundUser.id)) {
          mentionedUserIds.push(foundUser.id);
        }
      });
    }

    // Find hashtags (including emojis)
    let hashtags = postText.match(/#[\p{L}\p{N}_\p{Emoji}]+/gu);

    // Clean hashtags by removing the `#`
    hashtags = hashtags ? hashtags.map((hashtag) => hashtag.slice(1)) : [];

    setIsPosting(true);

    // Initialize FormData
    const formData = new FormData();

    // Append text content and privacy
    formData.append("post_type", privacy);
    formData.append("text_content", postText);

    // Append files with explicit array syntax (media_content[])
    mediaPreviews.forEach((media) => {
      formData.append("media_content[]", media.file);
    });

    // Append only the mentioned user IDs (not all suggestions)
    mentionedUserIds.forEach((id) => {
      formData.append("tags[]", id);
    });

    // Append hashtags
    hashtags.forEach((hashtag) => {
      formData.append("hashtags[]", hashtag);
    });

    // Append type
    formData.append("type", selectedPostType);

    console.log("FormData before sending:");
    for (let pair of formData.entries()) {
      console.log(`${pair[0]}: ${pair[1]}`);
    }

    // Send FormData to the backend
    createPost(formData)
      .then((response) => {
        setPostText("");
        setMediaPreviews([]);
        setUrlPreview(null);
        handleCloseDialog();
        setIsPosting(false);
        setSelectedPostType("");
        setTaggedUsers([]);
        showSnackbar(response.success, "success");
        console.log("response", response);

        const {
          post,
          reaction_count,
          comment_count,
          share_count,
          all_reaction_list,
        } = response.data;

        const newPost = {
          id: post.id,
          unique_id: post.unique_id,
          text_content: post.text_content,
          created_at: post.created_at,
          user: {
            username: post.user.username,
            name: post.user.name,
            thumbnail_pic: getImageUrlWithToken(post.user.thumbnail_pic),
          },
          post_content:
            post.post_content?.map((content) =>
              content.media_content_blob ? content.media_content_blob : null
            ) || [],
          reaction_count: reaction_count,
          comment_count: comment_count,
          share_count: share_count,
          my_reaction: post.my_reaction || false,
          is_my_post: post.is_my_post || true,
          post_type: post.post_type,
          post_type_name: post.type,
          all_reaction_list: all_reaction_list || [],
          is_connected: "me",
          tagged_users: post.tags || [], // Add the tagged users here
        };

        console.log("newPost", newPost);
        setPrivacy(user?.post_type || "public");

        // Optimistically update the UI
        setPosts((prevPosts) => [newPost, ...prevPosts]);
      })
      .catch((error) => {
        console.error("Error creating post", error);
        showSnackbar(error.error || "Failed to create post", "error");
        setIsPosting(false);
      });
  };

  const removeMedia = (id) => {
    setMediaPreviews((prevPreviews) =>
      prevPreviews.filter((media) => media.id !== id)
    );
  };

  const privacyIcons = {
    public: <Public />,
    connection: <People />,
    private: <LockPerson />,
  };

  const [allSuggestions, setAllSuggestions] = useState([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [taggedUsers, setTaggedUsers] = useState([]);
  const [isFetchingPreview, setIsFetchingPreview] = useState(false);
  const [urlPreview, setUrlPreview] = useState(null);
  const [hashtagQuery, setHashtagQuery] = useState("");
  const [hashtagSuggestions, setHashtagSuggestions] = useState([]);
  const [showHashtagSuggestions, setShowHashtagSuggestions] = useState(false);
  const [isPlayableVideo, setIsPlayableVideo] = useState(false);
  const [extractedUrl, setExtractedUrl] = useState("");


  const handlePostTextChange = (e) => {
    let value = e.target.value;
    setPostText(value);

    //if the value is empty, reset the url preview
    if (!value.trim()) {
      setUrlPreview(null);
      setIsPlayableVideo(false);
      return;
    }

    const urls = value.match(urlRegex);
    if (urls && urls.length > 0) {
      const url = urls[0]; // Extract the first URL
      setExtractedUrl(url);
      const isVideo = videoRegex.test(url);
      setIsPlayableVideo(isVideo);
      if (isVideo) {
        setUrlPreview(null);
        return;
      }

      setIsFetchingPreview(true);
      getLinkPreview(url)
        .then((data) => {
          console.log("Link preview fetched", data);
          setUrlPreview({
            title: data.title,
            description: data.description,
            favicon: data.favicon,
            url: url,
          });
          setIsFetchingPreview(false);
        })
        .catch((error) => {
          console.error("Error fetching link preview", error);
          setIsFetchingPreview(false);
        });
    }
    const cursorPosition = e.target.selectionStart;
    const beforeCursor = value.slice(0, cursorPosition);

    // Detect '@' and # triggers
    const lastAt = beforeCursor.lastIndexOf("@");
    const lastHash = beforeCursor.lastIndexOf("#");
    if (lastAt !== -1 && (lastHash === -1 || lastAt > lastHash)) {
      setQuery(beforeCursor.slice(lastAt)); // Pass "@username"
      setShowHashtagSuggestions(false);
    } else if (lastHash !== -1 && (lastAt === -1 || lastHash > lastAt)) {
      const hashtagText = beforeCursor.slice(lastHash);
      // Check if this is a complete hashtag (has a space after it)
      const textAfterHashtag = value.slice(cursorPosition);
      const isCompleteHashtag =
        /^#\w+\s/.test(hashtagText) || textAfterHashtag.startsWith(" ");

      if (!isCompleteHashtag) {
        setHashtagQuery(hashtagText);
      } else {
        setHashtagQuery("");
        setShowHashtagSuggestions(false);
      }
      setQuery("");
    } else {
      setQuery("");
      setHashtagQuery("");
      setShowHashtagSuggestions(false);
    }
  };

  // Insert the selected username into the post text
  const handleInsertUser = (username) => {
    const cursorPosition = postText.lastIndexOf("@");
    const beforeAt = postText.slice(0, cursorPosition);
    const afterAt = postText.slice(cursorPosition + query.length);

    setPostText(`${beforeAt}${username} ${afterAt}`); // Insert username without '@'
    setQuery("");

    // Save the tagged user so we don't lose them when allSuggestions changes
    const cleanUsername = username.startsWith("@") ? username.slice(1) : username;
    const taggedUser = allSuggestions.find(
      (person) => person.username === cleanUsername
    );
    if (taggedUser) {
      setTaggedUsers((prev) => {
        if (prev.some((u) => u.id === taggedUser.id)) return prev;
        return [...prev, taggedUser];
      });
    }
  };

  // Add hashtag selection handler
  const handleHashtagSelect = (hashtagObj) => {
    const cursorPosition = postText.lastIndexOf("#");
    const beforeHash = postText.slice(0, cursorPosition);
    const afterHash = postText.slice(cursorPosition + hashtagQuery.length);

    setPostText(`${beforeHash}#${hashtagObj.hashtag.name} ${afterHash}`); // Insert hashtag without '#'
    setHashtagQuery("");
    setShowHashtagSuggestions(false);
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (query && query.startsWith("@")) {
        getPeople(query)
          .then((data) => {
            console.log("Suggestions fetched", data);
            const processedData = data.map((person) => ({
              ...person,
              thumbnail_pic: person.thumbnail_pic
                ? getImageUrlWithToken(person.thumbnail_pic)
                : null,
            }));
            setAllSuggestions(processedData);
            setFilteredSuggestions(processedData);
          })
          .catch((error) => console.error("Error fetching suggestions", error));
      } else {
        setFilteredSuggestions([]);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(debounceTimer);
  }, [query, user]);

  useEffect(() => {
    if (query.startsWith("@")) {
      const searchText = query.slice(1).toLowerCase();
      const filtered = allSuggestions.filter(
        (person) =>
          person.username.toLowerCase().includes(searchText) ||
          person.name.toLowerCase().includes(searchText)
      );
      setFilteredSuggestions(filtered);
    } else {
      setFilteredSuggestions([]);
    }
  }, [query, allSuggestions]);

  // Add useEffect for hashtag suggestions
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (
        hashtagQuery &&
        hashtagQuery.startsWith("#") &&
        hashtagQuery.length > 3
      ) {
        const newQuery = hashtagQuery.slice(1);
        getHashtags(newQuery)
          .then((data) => {
            setHashtagSuggestions(data);
            setShowHashtagSuggestions(true);
          })
          .catch(() => {
            setHashtagSuggestions([]);
            setShowHashtagSuggestions(false);
          });
      } else {
        setShowHashtagSuggestions(false);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [hashtagQuery]);


  const [forceOpen, setForceOpen] = useState(false);

  useEffect(() => {
    let showTimer;
    let hideTimer;

    if (openDialog) {
      // Wait 2 seconds before showing the tooltip
      showTimer = setTimeout(() => {
        setForceOpen(true);

        // Hide the tooltip after 3 seconds
        hideTimer = setTimeout(() => {
          setForceOpen(false);
        }, 3000);
      }, 2000);
    }

    // Cleanup both timers
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [openDialog]);





  return (
    <React.Fragment>
      {/* Create Post Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullScreen={isSmallScreen}
        maxWidth={isSmallScreen ? false : "md"}
        fullWidth={!isSmallScreen}
        aria-hidden={!openDialog}
        TransitionComponent={Transition}
        aria-labelledby="dialog-title"
        aria-describedby="dialog-description"
        role="dialog"
      >
        <IconButton
          onClick={handleCloseDialog}
          sx={{
            position: "absolute",
            right: { xs: 4, sm: 8 },
            top: { xs: 4, sm: 8 },
          }}
        >
          <Close />
        </IconButton>

        <DialogTitle
          variant="h6"
          id="dialog-title"
          sx={{ p: 1, bgcolor: "background.paper" }}
        >
          Create Post
        </DialogTitle>

        <DialogContent
          dividers
          id="dialog-description"
          sx={{
            p: { xs: 1, sm: 3 },
            bgcolor: "background.paper"
          }}
        >
          <List dense disablePadding>
            <ListItem
              disableGutters
              sx={{
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
              }}
              secondaryAction={
                // Show secondaryAction on tablet and desktop (sm and above)
                !isSmallScreen ? (
                  <CustomizedTooltips forceOpen={forceOpen} setForceOpen={setForceOpen}>
                    <FormControl size="small">
                      <InputLabel id="post-type-label">Add Tags</InputLabel>
                      <Select
                        sx={{
                          minWidth: 160,
                          "& .MuiSelect-icon": {
                            display: selectedPostType ? "none" : "block",
                          },
                        }}
                        labelId="post-type-label"
                        label="Add Tags"
                        onChange={(e) => handlePostTypeSelect(e.target.value)}
                        value={selectedPostType}
                        renderValue={(selected) => {
                          const selectedType = types.find(
                            (type) => type.post_type_name === selected
                          );
                          return selectedType ? (
                            <Chip
                              clickable={false}
                              onClick={() => { }}
                              label={selectedType.post_type_name}
                              size="small"
                            />
                          ) : (
                            <em>Select Post Type</em>
                          );
                        }}
                        endAdornment={
                          selectedPostType && (
                            <IconButton onClick={() => setSelectedPostType("")} size="small">
                              <Close />
                            </IconButton>
                          )
                        }
                      >
                        <MenuItem disabled value="" dense>
                          <em>Select Post Type</em>
                        </MenuItem>
                        {types.map((type) => (
                          <MenuItem
                            dense
                            key={type.post_type_id}
                            value={type.post_type_name}
                          >
                            {type.post_type_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </CustomizedTooltips>
                ) : null
              }
            >
              {/* Main content wrapper */}
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <ListItemAvatar
                  sx={{
                    minWidth: 35,
                    mr: 1,
                  }}
                >
                  {user?.is_mentor ? (
                    <MentorBadge isSmall>
                      <Avatar src={user?.thumbnail_pic} alt={user?.name} sx={{ border: "1px solid", borderColor: "divider" }} />
                    </MentorBadge>
                  ) : (
                    <Avatar src={user?.thumbnail_pic} alt={user?.name} sx={{ border: "1px solid", borderColor: "divider" }} />
                  )}
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body1" noWrap sx={{ fontSize: 14}}>
                        {user?.name}
                      </Typography>
                      {user?.is_verified && (
                        <VerifiedUser fontSize="small" color="success" sx={{ ml: 0.5, fontSize: 16}} />
                      )}
                    </Box>
                  }
                  secondary={
                    <React.Fragment>
                      <Button
                        size="small"
                        onClick={handlePrivacyClick}
                        startIcon={privacyIcons[privacy]}
                        endIcon={<ArrowDropDown />}
                        sx={{ textTransform: "none" }}
                      >
                        {privacy.charAt(0).toUpperCase() + privacy.slice(1)}
                      </Button>
                    </React.Fragment>
                  }
                  slotProps={{
                    primary: { noWrap: true, fontSize: 14 },
                  }}
                />
              </Box>

              {/* Mobile Add Tags - Only show on mobile (xs) */}
              {isSmallScreen && (
                <Box sx={{ width: '100%', mt: 1 }}>
                  <CustomizedTooltips forceOpen={forceOpen} setForceOpen={setForceOpen}>
                    <FormControl size="small" fullWidth>
                      <InputLabel id="post-type-label-mobile">Add Tags</InputLabel>
                      <Select
                        sx={{
                          "& .MuiSelect-icon": {
                            display: selectedPostType ? "none" : "block",
                          },
                        }}
                        labelId="post-type-label-mobile"
                        label="Add Tags"
                        onChange={(e) => handlePostTypeSelect(e.target.value)}
                        value={selectedPostType}
                        renderValue={(selected) => {
                          const selectedType = types.find(
                            (type) => type.post_type_name === selected
                          );
                          return selectedType ? (
                            <Chip
                              clickable={false}
                              onClick={() => { }}
                              label={selectedType.post_type_name}
                              size="small"
                            />
                          ) : (
                            <em>Select Post Type</em>
                          );
                        }}
                        endAdornment={
                          selectedPostType && (
                            <IconButton onClick={() => setSelectedPostType("")} size="small">
                              <Close />
                            </IconButton>
                          )
                        }
                      >
                        <MenuItem disabled value="" dense>
                          <em>Select Post Type</em>
                        </MenuItem>
                        {types.map((type) => (
                          <MenuItem
                            dense
                            key={type.post_type_id}
                            value={type.post_type_name}
                          >
                            {type.post_type_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </CustomizedTooltips>
                </Box>
              )}
            </ListItem>
          </List>

          {/* Privacy Menu */}
          <Menu
            anchorEl={privacyAnchorEl}
            open={Boolean(privacyAnchorEl)}
            onClose={handlePrivacyClose}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  borderRadius: 2,
                  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.1)",
                },
              },
            }}
          >
            <MenuItem
              onClick={() => handlePrivacyChange("public")}
              selected={privacy === "public"}
            >
              <Public sx={{ mr: 2, color: "text.secondary" }} /> Public
            </MenuItem>
            <MenuItem
              onClick={() => handlePrivacyChange("connections")}
              selected={privacy === "connections" || privacy === "connection"}
            >
              <People sx={{ mr: 2, color: "text.secondary" }} /> Connections
            </MenuItem>
            <MenuItem
              onClick={() => handlePrivacyChange("private")}
              selected={privacy === "private"}
            >
              <LockPerson sx={{ mr: 2, color: "text.secondary" }} /> Private
            </MenuItem>
          </Menu>

          <Paper variant="outlined" sx={{ p: 1, mt: 1 }}>
            <InputBase
              multiline
              rows={6}
              fullWidth
              placeholder="What's on your mind?"
              value={postText}
              onChange={handlePostTextChange}
            />
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Tooltip title="Galley" placement="bottom" arrow>
                <span>
                  <input
                    id="media-input"
                    multiple
                    type="file"
                    // accept="image/*, video/*"
                    //only accept image files
                    accept="image/*"
                    hidden
                    onChange={handleMediaChange}
                    onClick={(e) => (e.target.value = null)}
                  />
                  <label htmlFor="media-input">
                    <IconButton component="span">
                      <PhotoLibrary />
                    </IconButton>
                  </label>
                </span>
              </Tooltip>

              <CustomEmojiPicker onSelect={handleEmojiSelect} />
            </Box>

            {filteredSuggestions.length > 0 && (
              <List
                sx={{
                  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.1)",
                  maxHeight: 240,
                  overflowY: "auto",
                  scrollbarWidth: "thin",
                }}
              >
                {filteredSuggestions.map((person) => (
                  <ListItemButton
                    key={person.id}
                    onClick={() => handleInsertUser(`@${person.username}`)}
                  >
                    <ListItemAvatar>
                      {person.is_mentor ? (
                        <MentorBadge isSmall>
                          <Avatar src={person.thumbnail_pic} alt={person.name} sx={{ border: "1px solid", borderColor: "divider" }} />
                        </MentorBadge>
                      ) : (
                        <Avatar src={person.thumbnail_pic} alt={person.name} sx={{ border: "1px solid", borderColor: "divider" }} />
                      )}
                    </ListItemAvatar>
                    <ListItemText
                      primary={person.name}
                      secondary={person.describes_me}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}

            {/* hashtagSuggestions */}

            {showHashtagSuggestions && hashtagSuggestions.length > 0 && (
              <List
                sx={{
                  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.1)",
                  maxHeight: 240,
                  overflowY: "auto",
                  scrollbarWidth: "thin",
                }}
              >
                {hashtagSuggestions.map((hashtag) => (
                  <ListItemButton
                    key={hashtag.hashtag.id}
                    onClick={() => handleHashtagSelect(hashtag)}
                  >
                    <ListItemText
                      primary={`#${hashtag.hashtag.name}`}
                      secondary={`${hashtag.usage_count} ${parseInt(hashtag.usage_count) === 1
                        ? "mention"
                        : "mentions"
                        }`}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}


            {isFetchingPreview ? (
              <Box display="flex" justifyContent="center" mt={2}>
                <CircularProgress size={20} sx={{ ml: 1 }} />
              </Box>
            ) : isPlayableVideo ? (
              <Box sx={{ mt: 2 }} >
                <VideoPreview watchLink={extractedUrl}  />
              </Box>
            ) : urlPreview ? (
              <Box
                component="a"
                href={urlPreview.url}
                target="_blank"
                rel="noreferrer"
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  p: 1,
                  borderRadius: 2,
                  textDecoration: "none",
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  mt: 2,
                }}
              >
                {urlPreview.favicon ? (
                  <Box
                    sx={{
                      width: 60, // Set your desired dimensions
                      height: 60,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      component="img"
                      src={urlPreview.favicon}
                      alt={urlPreview.title}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                ) : (
                  <BeachAccess />
                )}

                {/* Text Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    noWrap
                    color="text.primary"
                  >
                    {urlPreview.title || "No title"}
                  </Typography>
                  <Typography variant="body2" noWrap color="textSecondary">
                    {urlPreview.description || "No description"}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textDisabled"
                    sx={{
                      display: "block",
                      mt: 0.5,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {urlPreview.url}
                  </Typography>
                </Box>
              </Box>
            ) : null}

          </Paper>

          {mediaPreviews.length > 0 && (
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                flexWrap: "wrap",
                mt: 2,
                justifyContent: "center",
              }}
            >
              {mediaPreviews.map((media) => (
                <MediaPreviewBox key={media.id}>
                  {media.type === "image" ? (
                    <img
                      src={media.preview}
                      alt="preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <video
                      src={media.preview}
                      controls
                      style={{ width: "100%", height: "100%" }}
                    />
                  )}
                  <IconButton
                    className="media-remove"
                    onClick={() => removeMedia(media.id)}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      bgcolor: "rgba(0, 0, 0, 0.6)",
                      color: "white",
                      opacity: 0.8,
                      "&:hover": { bgcolor: "rgba(0, 0, 0, 0.9)" },
                    }}
                  >
                    <Close />
                  </IconButton>
                </MediaPreviewBox>
              ))}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 1, bgcolor: "background.paper" }}>
          <Button
            fullWidth
            variant="contained"
            disabled={
              isPosting || (!postText.trim() && mediaPreviews.length === 0)
            }
            onClick={handlePost}
            disableElevation
            sx={{ textTransform: "none" }}
          >
            {isPosting ? <CircularProgress size={24} /> : "Post"}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default MediaPost;
